import { Component, OnInit, ChangeDetectionStrategy, inject, signal, input } from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {CampaignService} from '@app/services/campaign.service';
import {UserAbilityService} from '@app/services/user-ability.service';
import {smartDateFormat} from '@app/consts';

@Component({
  selector: 'app-campaign-launch-history',
  templateUrl: './campaign-launch-history.component.html',
  styleUrls: ['./campaign-launch-history.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CampaignLaunchHistoryComponent implements OnInit {
  modal = inject(NgbActiveModal);
  private campaignService = inject(CampaignService);
  private userAbility = inject(UserAbilityService);

  readonly campaignId = input<number>(undefined);
  readonly campaignName = input<string>(undefined);
  readonly launchHistory = signal<any[]>([]);
  readonly loading = signal(true);
  isStaff = false;
  readonly statusLegend = signal<any[]>([]);
  readonly timelineEvents = signal<any[]>([]);
  readonly timelineNotes = signal<string[]>([]);
  readonly timelineUnavailable = signal(false);
  showLegend = false;
  legendTop = 0;
  legendLeft = 0;

  constructor() {
    this.isStaff = this.userAbility.can('launchHistory', 'campaign');
  }

  ngOnInit(): void {
    this.loadHistory();
  }

  loadHistory() {
    this.loading.set(true);
    this.campaignService.getLaunchHistory(this.campaignId()).subscribe(
      (resp: any) => {
        if (resp.success) {
          this.launchHistory.set(resp.data?.history || []);
          this.statusLegend.set(resp.data?.statusLegend || []);
          this.timelineEvents.set(resp.data?.timeline?.events || []);
          this.timelineNotes.set(resp.data?.timeline?.notes || []);
          this.timelineUnavailable.set(!!resp.data?.timeline?.unavailable);
        }
        this.loading.set(false);
      },
      () => {
        this.loading.set(false);
      }
    );
  }

  smartDateFormat(datetime) {
    return smartDateFormat(datetime);
  }

  onBadgeHover(event: MouseEvent) {
    const el = event.target as HTMLElement;
    const rect = el.getBoundingClientRect();
    this.legendTop = rect.bottom + 4;
    this.legendLeft = Math.max(rect.left - 300, 8);
    this.showLegend = true;
  }

  get statusLegendPairs(): any[][] {
    const pairs = [];
    for (let i = 0; i < this.statusLegend().length; i += 2) {
      pairs.push([this.statusLegend()[i], this.statusLegend()[i + 1] || null]);
    }
    return pairs;
  }

  getResultBadgeClass(result: string): string {
    switch (result) {
      case 'launched': return 'text-bg-success';
      case 'launched_partial': return 'text-bg-warning';
      case 'failed': return 'text-bg-danger';
      case 'cancelled': return 'text-bg-warning';
      case 'scheduled': return 'text-bg-info';
      case 'queued': return 'text-bg-primary';
      case 'launching': return 'text-bg-info';
      case 'launching_slow': return 'text-bg-warning';
      default: return 'text-bg-secondary';
    }
  }

  getEventLabel(type: string): string {
    switch (type) {
      case 'schedule_set': return 'Scheduled';
      case 'launch_requested': return 'Launch requested';
      case 'launch_cancelled': return 'Cancelled';
      case 'request_refused': return 'Refused';
      case 'status': return 'Status';
      case 'job_queued': return 'Job queued';
      case 'attempt_started': return 'Sending';
      case 'job': return 'Job';
      case 'sync_started': return 'Sync started';
      case 'sync_finished': return 'Sync finished';
      case 'sync_failed': return 'Sync failed';
      case 'launch_cancelled_by_sync': return 'Cancelled by sync';
      default: return type?.replace(/_/g, ' ');
    }
  }

  getEventBadgeClass(severity: string): string {
    switch (severity) {
      case 'error': return 'text-bg-danger';
      case 'warning': return 'text-bg-warning';
      default: return 'text-bg-secondary';
    }
  }

  getEventActor(event: any): string {
    if (!event.actor) {
      return '-';
    }
    return event.actor.name || ('User ' + event.actor.id);
  }

  getResultDescription(result: string): string {
    const item = this.statusLegend().find(s => s.result === result);
    return item?.description || '';
  }

  getResultLabel(result: string): string {
    switch (result) {
      case 'launched_partial': return 'Launched (Partial)';
      case 'launching_slow': return 'Launching (Slow)';
      default: return result?.replace(/_/g, ' ');
    }
  }
}
